import { FieldOption, OperatorOption } from '../constants';
import { getTextValueOperator } from './helpers';
import {
  expandAnimalIdentChip,
  hasWildcardChars,
  isEdgeOnlyWildcardPattern,
} from './animalIdentChipFilter';

/** A display-only choice. Stored legacy patterns retain their original operator. */
export const IDENTIFIER_PATTERN_OPERATOR = '__identifier_pattern__';

export const IDENTIFIER_PATTERN_HELP =
  'Edit this saved pattern, or choose an operator to start a new filter. % matches any text; commas separate alternatives.';

export function isIdentifierOperatorField(field: string): boolean {
  return (
    field === FieldOption.tattoo.value || field === FieldOption.herdPrefix.value
  );
}

type IdentifierFilter = { operator: string; value: string };

/** Project legacy values for editing without changing a saved filter on mount. */
export function getIdentifierOperatorFilter(
  operator = '',
  value = '',
): IdentifierFilter & { isLegacyPattern: boolean } {
  const hasPatternSyntax = value.includes(',') || hasWildcardChars(value);
  if (operator) {
    // These saved rows use the existing wildcard parser, which takes precedence
    // over a stored operator. Keep that meaning visible until explicitly replaced.
    return { operator, value, isLegacyPattern: hasPatternSyntax };
  }
  if (!value.trim()) {
    return {
      operator: '',
      value,
      isLegacyPattern: false,
    };
  }
  if (
    !value.includes(',') &&
    (!hasWildcardChars(value) || isEdgeOnlyWildcardPattern(value))
  ) {
    const expanded = expandAnimalIdentChip(value);
    if (expanded.value) return { ...expanded, isLegacyPattern: false };
  }
  return { operator, value, isLegacyPattern: true };
}

/** Commit a user edit using the projection, leaving untouched saved rows intact. */
export function editIdentifierOperatorFilter(
  operator: string,
  value: string,
  patch: Partial<IdentifierFilter>,
): IdentifierFilter {
  const display = getIdentifierOperatorFilter(operator, value);
  if (patch.operator != null) {
    return {
      operator: patch.operator,
      value:
        display.isLegacyPattern ||
        patch.operator === OperatorOption.isNotEmpty.value
          ? ''
          : display.value,
    };
  }
  return {
    operator: display.isLegacyPattern
      ? operator
      : getTextValueOperator(display.operator, patch.value ?? display.value),
    value: patch.value ?? display.value,
  };
}
